import type { CashflowResultWithLatestResult } from "@/view_models/balancesheetTypes";

export type MonthlyCashflowAmounts = {
  unroundedIncome: number;
  unroundedExpense: number;
  rentIncome: number;
  /** 管理費収入（その他収入を除いた分） */
  managementFeeIncomeOnly: number;
  otherIncome: number;
  loanRepayment: number;
  // 区分マンションの月額経費
  buildingManagementCost: number;
  rentalManagementCost: number;
  repairFundCost: number;
  communicationCost: number;
  miscCost: number;
  // 一棟物件の建物経費
  buildingManagementCompanyCommission: number;
  buildingSharedUtilityExpenses: number;
  buildingMiscCost: number;
};

/**
 * 詳細・マイページで共有する、丸め前の当月収支と内訳。
 *
 * 収入・経費とも月次計算結果（購入月は購入日からの日割り、実績更新の入力はその月以降に反映）をそのまま使い、
 * ここで入力値や契約情報から再計算しない。累積収支・収支シミュレーションと同じ値を見せるため。
 */
export const buildMonthlyCashflowAmounts = (
  propertyCategory: string | undefined,
  result: Partial<CashflowResultWithLatestResult>
): MonthlyCashflowAmounts => {
  const isBuilding = propertyCategory === "一棟物件";

  const rentIncome = result.rent_income || 0;
  const managementFeeIncome = result.management_fee_income || 0;
  const otherIncome = result.other_income || 0;
  const managementFeeIncomeOnly = isBuilding ? 0 : Math.max(0, managementFeeIncome - otherIncome);

  const loanRepayment = (result.loan_principal || 0) + (result.loan_interest || 0) + (result.loan_prepayment || 0);

  // 一棟の登録・編集 UI には賃貸管理費・建物管理費・修繕積立金・通信費の入力項目がなく常に 0 のため、
  // 一棟では建物経費とローンのみを支出とする（累積収支側は 0 を加算するだけなので結果は一致する）。
  const buildingManagementCost = isBuilding ? 0 : result.building_management_cost || 0;
  const rentalManagementCost = isBuilding ? 0 : result.management_cost || 0;
  const repairFundCost = isBuilding ? 0 : result.repair_fund_cost || 0;
  const communicationCost = isBuilding ? 0 : result.communication_cost || 0;
  const miscCost = isBuilding ? 0 : result.misc_cost || 0;

  const buildingManagementCompanyCommission = isBuilding ? result.building_management_company_commission || 0 : 0;
  const buildingSharedUtilityExpenses = isBuilding ? result.building_shared_utility_expenses || 0 : 0;
  const buildingMiscCost = isBuilding ? result.building_misc_cost || 0 : 0;

  // 収入は区分・一棟とも 家賃 + 管理費収入（その他収入を含む）
  const unroundedIncome = rentIncome + managementFeeIncome;
  const unroundedExpense =
    loanRepayment +
    buildingManagementCost +
    rentalManagementCost +
    repairFundCost +
    communicationCost +
    miscCost +
    buildingManagementCompanyCommission +
    buildingSharedUtilityExpenses +
    buildingMiscCost;

  return {
    unroundedIncome,
    unroundedExpense,
    rentIncome,
    managementFeeIncomeOnly,
    otherIncome,
    loanRepayment,
    buildingManagementCost,
    rentalManagementCost,
    repairFundCost,
    communicationCost,
    miscCost,
    buildingManagementCompanyCommission,
    buildingSharedUtilityExpenses,
    buildingMiscCost,
  };
};

/** 物件別・全物件合計とも、集計と差引を終えてから円単位に丸める。 */
export const roundMonthlyCashflow = ({
  unroundedIncome,
  unroundedExpense,
}: Pick<MonthlyCashflowAmounts, "unroundedIncome" | "unroundedExpense">) => ({
  income: Math.round(unroundedIncome),
  expense: Math.round(unroundedExpense),
  totalCashFlow: Math.round(unroundedIncome - unroundedExpense),
});

export const buildThisMonthCashflow = (
  propertyCategory: string | undefined,
  result: Partial<CashflowResultWithLatestResult>
) => {
  const amounts = buildMonthlyCashflowAmounts(propertyCategory, result);
  return { ...amounts, ...roundMonthlyCashflow(amounts) };
};
